// Asset lifecycle rules shared by web and mobile (CR_20260921_Asset_Lifecycle): certificate states, the asset-docs
// file rules, and reading the server's refusal at dispatch (L6 / L6a). The database decides every one of these —
// this is only how the apps show them.

export type CertState = 'valid' | 'expiring' | 'expired' | 'missing';

/** One row of public.asset_cert_status: a kind the asset's category requires or it holds, with its latest certificate. */
export interface CertStatusRow {
  asset_id: string;
  company_id: string;
  kind: string;
  required: boolean;
  blocks_dispatch: boolean;
  remind_days_before: number;
  valid_for_days: number | null;
  certificate_id: string | null;
  number: string | null;
  issuer: string | null;
  issued_on: string | null;
  expires_on: string | null;
  document_path: string | null;
  note: string | null;
  state: CertState;
  days_left: number | null;
}

/** Worst first: what a list should sort by and a summary should show. */
export const CERT_STATE_ORDER: readonly CertState[] = ['expired', 'missing', 'expiring', 'valid'];

/**
 * The one state an asset shows for its certificates: the worst of its REQUIRED kinds (a certificate no rule asks
 * for never makes an asset non-compliant, but its own expiry still shows on the card). Null = nothing required.
 */
export function worstCertState(
  rows: readonly Pick<CertStatusRow, 'required' | 'state'>[],
): CertState | null {
  const required = rows.filter((r) => r.required);
  if (!required.length) return null;
  return CERT_STATE_ORDER.find((s) => required.some((r) => r.state === s)) ?? 'valid';
}

/** Card order: required kinds first, then by state (worst first), then by name. */
export function sortCertRows<T extends Pick<CertStatusRow, 'required' | 'state' | 'kind'>>(
  rows: readonly T[],
): T[] {
  return [...rows].sort(
    (a, b) =>
      Number(b.required) - Number(a.required) ||
      CERT_STATE_ORDER.indexOf(a.state) - CERT_STATE_ORDER.indexOf(b.state) ||
      a.kind.localeCompare(b.kind),
  );
}

/** Kinds are compared the way the database does: trimmed, case-insensitive. */
export const sameKind = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Suggestions for the kind field: every kind already in use, once each (first spelling wins), sorted. */
export function kindSuggestions(kinds: readonly string[]): string[] {
  const seen = new Map<string, string>();
  for (const k of kinds) {
    const v = k.trim();
    if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** "Valid until" from "issued on" + the rule's "valid for N days" (the dialog's prefill; the user may change it). */
export function expiryFrom(
  issuedOn: string,
  validForDays: number | null | undefined,
): string | null {
  if (!issuedOn || !validForDays) return null;
  const d = new Date(`${issuedOn}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + validForDays);
  return d.toISOString().slice(0, 10);
}

// ------------------------------------------------------------------------------------------------ asset-docs files
export const DOC_BUCKET = 'asset-docs';
/** The bucket's limit (L7). */
export const DOC_MAX_BYTES = 10 * 1024 * 1024;
export const DOC_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'] as const;
export type DocFolder = 'certs' | 'service' | 'condition';

const EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

/** `<company_id>/<folder>/<file id>.<ext>` — the storage policies check the first two folders. */
export function docPath(
  companyId: string,
  folder: DocFolder,
  fileId: string,
  contentType: string,
): string {
  return `${companyId}/${folder}/${fileId}.${EXT[contentType] ?? 'bin'}`;
}

/** Why a file cannot be attached, or null when it can. */
export function docProblem(file: { size: number; type: string }): 'too_big' | 'type' | null {
  if (!(DOC_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size > DOC_MAX_BYTES) return 'too_big';
  return null;
}

// ------------------------------------------------------------------------------------------------ dispatch refusal
export interface DispatchBlocker {
  asset_id: string;
  code: string;
  kind: string;
  /** 'missing' | 'expired' (certificates, T1) · 'out_of_service' (T2) */
  reason: string;
  expires_on: string | null;
  overridable: boolean;
}

export interface DispatchBlock {
  /** the server's own sentence, after "dispatch blocked: " */
  text: string;
  /** parsed from the error's DETAIL when present */
  blockers: DispatchBlocker[];
  /** a manager may send it anyway with a reason */
  overridable: boolean;
}

const PREFIX = 'dispatch blocked: ';

/** Is this failure message the certificate / service refusal at dispatch? (The phone queue keeps only the message.) */
export function isDispatchBlockMessage(message: string | null | undefined): boolean {
  return !!message && message.startsWith(PREFIX);
}

/** Read a refusal from a PostgREST error (code 55000, message "dispatch blocked: …", details = JSON, hint). */
export function dispatchBlock(
  err:
    | { code?: string; message?: string; details?: string | null; hint?: string | null }
    | null
    | undefined,
): DispatchBlock | null {
  if (!err || !isDispatchBlockMessage(err.message)) return null;
  let blockers: DispatchBlocker[] = [];
  try {
    const parsed: unknown = err.details ? JSON.parse(err.details) : [];
    if (Array.isArray(parsed)) blockers = parsed as DispatchBlocker[];
  } catch {
    blockers = [];
  }
  const overridable =
    err.hint === 'overridable' ||
    (err.hint == null && blockers.length > 0 && blockers.every((b) => b.overridable));
  return { text: err.message!.slice(PREFIX.length), blockers, overridable };
}

// ------------------------------------------------------------------------------------------------ condition (T2)
/** The four words (L11), best first. */
export const CONDITIONS = ['good', 'fair', 'poor', 'unserviceable'] as const;
export type AssetCondition = (typeof CONDITIONS)[number];
export type ConditionSource = 'count' | 'receipt' | 'dispatch' | 'standalone';

/** The chips picked on a receipt or a dispatch, as the RPCs take them; null when nothing was picked (old behaviour). */
export function conditionsPayload(
  picked: ReadonlyMap<string, AssetCondition> | Readonly<Record<string, AssetCondition>>,
): Record<string, AssetCondition> | null {
  const entries = picked instanceof Map ? [...picked] : Object.entries(picked);
  return entries.length ? Object.fromEntries(entries) : null;
}

/** "good ▸ good ▸ fair ▸ fair": the last few reports, oldest first. */
export function conditionTrend(
  reports: readonly { condition: AssetCondition; at: string }[],
  last = 4,
): AssetCondition[] {
  return [...reports]
    .sort((a, b) => a.at.localeCompare(b.at))
    .slice(-last)
    .map((r) => r.condition);
}

// ------------------------------------------------------------------------------------------------ out of service (T2)
/**
 * Who may mark an asset out of service or back (§4.5, app.can_set_service_state): a manager, or warehouse staff who
 * work at its custody site — never a project user. The server decides; the apps only mirror it.
 */
export function canSetServiceState(
  me:
    | {
        role: string | null;
        platform_admin?: boolean;
        site_access?: string | null;
        site_ids?: readonly string[];
        company?: unknown;
      }
    | undefined,
  custodySiteId: string | null | undefined,
): boolean {
  if (!me?.company) return false;
  if (me.role === 'manager') return true;
  if (me.role !== 'warehouse_staff' || !custodySiteId) return false;
  return me.site_access === 'all' || !!me.site_ids?.includes(custodySiteId);
}
